// pages/ScenariosListPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Box,
  IconButton,
  useTheme,
  Container,
  Button,
  alpha,
  Chip,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { getScenarios } from "services/api";
import LoadingScreen from "components/Loading";
import { useTranslation } from "react-i18next";
import {
  AccountTreeOutlined,
  Add,
  AddCircle,
  Delete,
  DeleteOutline,
  FolderOpen,
  Insights,
  LocalHospital,
  MonetizationOnOutlined,
} from "@mui/icons-material";
import AddScenarioDialog from "components/AddScenarioDialog";
import { addScenario } from "services/api";
import ConfirmationDialog from "components/ConfirmationDialog";
import { deleteScenario } from "services/api";
import { useAuth } from "context/authContext";
import { cloneScenario } from "services/api";
import { cloneScenarioFromCompany } from "services/api";
import MDButton from "components/MDButton";

const ScenariosListPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { employee } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getScenarios(employee.id)
      .then((data) => {
        if (data == "error") return;
        setScenarios(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching scenarios:", err);
        setLoading(false);
      });
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSave = async (payload) => {
    let result;

    // We extract the base data we need for all scenarios
    const baseData = {
      newName: payload.scenarioName,
      employeeId: employee.id, // Current logged-in user
      budget: payload.scenarioBudget,
      type: payload.type,
    };

    try {
      if (payload.isCloning) {
        if (payload.fromCompany) {
          // CASE 1: Clone from Company Data
          baseData.departmentId = payload.departmentId;
          result = await cloneScenarioFromCompany(baseData);
        } else {
          // CASE 2: Clone from an existing Scenario
          result = await cloneScenario(payload.sourceScenarioId, baseData);
        }
      } else {
        //CASE 3: Standard creation (Empty scenario)
        result = await addScenario({
          ...payload,
          employeeuId: employee.id,
        });
      }

      // Success Handling
      if (result && result !== "error") {
        setScenarios([...scenarios, result]);
      }
    } catch (error) {
      console.error("Failed to create/clone scenario:", error);
      // Optional: Trigger a notification/toast here
    } finally {
      setOpen(false);
    }
  };

  const handleDeleteClick = (scenario) => {
    setSelectedScenario(scenario);
    setOpenConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedScenario) {
      const res = await deleteScenario(selectedScenario.id); // hook this to your API
      if (res != "error") {
        setScenarios((prev) => prev.filter((sc) => sc.id !== selectedScenario.id));
      }
    }
    setOpenConfirm(false);
    setSelectedScenario(null);
  };

  if (loading) return <LoadingScreen />;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* 1. Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={5}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            {t("user.scenarios.chooseScenario")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t("user.scenarios.pageSubtitle")}
          </Typography>
        </Box>

        <MDButton
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleOpen}
          sx={{
            textTransform: "none",
            boxShadow: 4,
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: "bold",
          }}
        >
          {t("user.scenarios.createBtn")}
        </MDButton>
      </Box>

      {/* 2. Scenarios Grid */}
      {scenarios.length > 0 ? (
        <Grid container spacing={3}>
          {scenarios.map((scenario) => {
            const isGahar = scenario.type === "GAHAR Phase 1";

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={scenario.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    overflow: "hidden",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 12px 24px -4px rgba(0,0,0,0.1)",
                      borderColor: "primary.main",
                      "& .delete-btn": { opacity: 1, transform: "scale(1)" },
                    },
                  }}
                  onClick={() =>
                    navigate(`/manpower/simulation/scenarios`, { state: { scenario } })
                  }
                >
                  {/* Decorative Top Bar */}
                  <Box
                    sx={{
                      height: 6,
                      width: "100%",
                      bgcolor: isGahar ? "#108092" : "primary.main",
                    }}
                  />

                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    {/* Header: Type Chip & Delete */}
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Chip
                        icon={
                          isGahar ? (
                            <LocalHospital fontSize="small" />
                          ) : (
                            <AccountTreeOutlined fontSize="small" />
                          )
                        }
                        // label={scenario.type || "Standard"}
                        label={
                          scenario.type
                            ? t(`user.scenarios.scenarioTypes.${scenario.type}`)
                            : t("user.scenarios.scenarioTypes.Standard")
                        }
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: isGahar
                            ? alpha("#108092", 0.1)
                            : alpha(theme.palette.primary.main, 0.1),
                          color: isGahar ? "#108092" : "primary.main",
                          borderRadius: 1,
                        }}
                      />

                      {/* Delete Button (Hidden by default, shown on hover) */}
                      <IconButton
                        className="delete-btn"
                        size="small"
                        sx={{
                          opacity: 0, // Hidden by default
                          transform: "scale(0.8)",
                          transition: "all 0.2s",
                          color: "text.secondary",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            color: "error.main",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(scenario);
                        }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Box>

                    {/* Scenario Title */}
                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "10px",
                          bgcolor:
                            theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "grey.100",
                          color: "text.primary",
                        }}
                      >
                        <Insights fontSize="medium" />
                      </Box>
                      <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                        {scenario.scenarioName}
                      </Typography>
                    </Box>

                    {/* Budget Info */}
                    {!isGahar && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mt={2}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                          border: "1px dashed",
                          borderColor: "divider",
                        }}
                      >
                        <MonetizationOnOutlined fontSize="small" color="secondary" />
                        <Box>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            fontWeight="medium"
                          >
                            {t("user.scenarios.budget")}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {Number(scenario.scenarioBudget).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        /* 3. Empty State */
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="50vh"
          color="text.secondary"
        >
          <Box
            sx={{
              p: 3,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              mb: 2,
            }}
          >
            <FolderOpen sx={{ fontSize: 60, color: "primary.main", opacity: 0.5 }} />
          </Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            No Scenarios Found
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, maxWidth: 400, textAlign: "center", mb: 3 }}
          >
            Create your first manpower simulation scenario to start analyzing budgets and
            structures.
          </Typography>
          <MDButton variant="outlined" color="primary" startIcon={<Add />} onClick={handleOpen}>
            Create Scenario
          </MDButton>
        </Box>
      )}

      {/* Dialogs */}
      <AddScenarioDialog
        open={open}
        onClose={handleClose}
        onSave={handleSave}
        scenarios={scenarios}
      />
      <ConfirmationDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Scenario?"
        content="Are you sure you want to delete this scenario? This action cannot be undone."
      />
    </Container>
  );
};

export default ScenariosListPage;
